// Projeto: Haklai Church — Plugin WordPress Modular - Desenvolvido por: Ricardo Sarmento - https://linx.pt

import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import AppLayout from '../components/AppLayout';
import MemberSelector from '../components/MemberSelector';
import { MapContainer, TileLayer, Marker, useMapEvents, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { initLeafletIcons } from '../utils/leafletSetup';

// Componente helper para capturar cliques no mapa
function LocationMarker({ position, setPosition }) {
  useMapEvents({
    click(e) {
      setPosition(e.latlng);
    },
  });

  return position === null ? null : (
    <Marker position={position}>
      <Popup>Local do PIN para a Célula</Popup>
    </Marker>
  );
}

function CellFormPage() {
  initLeafletIcons();

  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const queryClient = useQueryClient();

  // Estados do formulário
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    address: '',
    neighborhood: '',
    latitude: '',
    longitude: '',
    day_of_week: '',
    start_time: '',
    max_capacity: '',
    is_active: true,
    hierarchy: {
      discipulador_id: null,
      pastor_rede_id: null,
      pastor_senior_id: null,
      pastor_supervisor_id: null,
    },
    leader_id: null,
  });

  const [errors, setErrors] = useState({});
  const [mapMarker, setMapMarker] = useState(null);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const mapRef = useRef(null);

  // Carrega célula se estiver editando
  const { data: cellData, isLoading: loadingCell } = useQuery({
    queryKey: ['cell', id],
    queryFn: async () => {
      const response = await api.get(`/cells/${id}`);
      return response.data.data;
    },
    enabled: isEdit,
  });

  // Carrega hierarquia da célula se estiver editando
  const { data: hierarchyData } = useQuery({
    queryKey: ['cell-hierarchy', id],
    queryFn: async () => {
      const response = await api.get(`/cells/${id}/hierarchy`);
      return response.data.data;
    },
    enabled: isEdit && !!id,
  });

  // Preenche formulário quando célula é carregada
  useEffect(() => {
    if (cellData && isEdit) {
      setFormData(prev => ({
        ...prev,
        name: cellData.name || '',
        description: cellData.description || '',
        address: cellData.address || '',
        neighborhood: cellData.neighborhood || '',
        latitude: cellData.latitude ? String(cellData.latitude) : '',
        longitude: cellData.longitude ? String(cellData.longitude) : '',
        day_of_week: cellData.day_of_week !== null && cellData.day_of_week !== undefined ? String(cellData.day_of_week) : '',
        start_time: cellData.start_time || '',
        max_capacity: cellData.max_capacity ? String(cellData.max_capacity) : '',
        is_active: cellData.is_active !== undefined ? Boolean(cellData.is_active) : true,
        leader_id: cellData.leader_id || null,
      }));

      if (cellData.latitude && cellData.longitude) {
        setMapMarker({ lat: parseFloat(cellData.latitude), lng: parseFloat(cellData.longitude) });
      }
    }
  }, [cellData, isEdit]);

  // Preenche hierarquia quando carregada
  useEffect(() => {
    if (hierarchyData && isEdit) {
      setFormData(prev => ({
        ...prev,
        hierarchy: {
          discipulador_id: hierarchyData.discipulador_id || null,
          pastor_rede_id: hierarchyData.pastor_rede_id || null,
          pastor_senior_id: hierarchyData.pastor_senior_id || null,
          pastor_supervisor_id: hierarchyData.pastor_supervisor_id || null,
        },
        leader_id: prev.leader_id || hierarchyData.leader_id || null,
      }));
    }
  }, [hierarchyData, isEdit]);

  // Mutation para criar/atualizar célula
  const cellMutation = useMutation({
    mutationFn: async (data) => {
      if (isEdit) {
        return api.put(`/cells/${id}`, data);
      } else {
        return api.post('/cells', data);
      }
    },
    onSuccess: async () => {

      // Invalida cache (incluindo hierarquia)
      queryClient.invalidateQueries({ queryKey: ['cells'] });
      queryClient.invalidateQueries({ queryKey: ['cell', id] });
      queryClient.invalidateQueries({ queryKey: ['cell-hierarchy', id] });
      queryClient.invalidateQueries({ queryKey: ['members-search'] });

      // Redireciona para a lista (CellDetailPage será criado na próxima fase)
      navigate('/cells');
    },
    onError: (error) => {
      const errorData = error.response?.data;
      if (errorData?.errors) {
        setErrors(errorData.errors);
      } else {
        setErrors({ general: errorData?.message || error.message || 'Erro ao salvar célula' });
      }
    },
  });

  // Handler de mudança de campo
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    // Atualiza marcador do mapa se mudar manualmente as coordenadas
    if (name === 'latitude' || name === 'longitude') {
      setMapMarker(prev => ({
        lat: name === 'latitude' ? parseFloat(value || 0) : (prev ? prev.lat : 0),
        lng: name === 'longitude' ? parseFloat(value || 0) : (prev ? prev.lng : 0)
      }));
    }

    // Limpa erro do campo ao modificar
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Handler para quando o usuário clica no mapa
  const handleMapClick = (latlng) => {
    setMapMarker(latlng);
    setFormData(prev => ({
      ...prev,
      latitude: String(latlng.lat),
      longitude: String(latlng.lng)
    }));
  };

  // Handler para buscar coordenadas a partir do endereço
  const handleGeocode = async () => {
    if (!formData.address) {
      setErrors(prev => ({ ...prev, address: 'Digite um endereço para buscar' }));
      return;
    }

    setIsGeocoding(true);
    try {
      // Usando Nominatim API (OpenStreetMap)
      const searchQuery = encodeURIComponent(`${formData.address}${formData.neighborhood ? ', ' + formData.neighborhood : ''}`);
      const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${searchQuery}&limit=1`, {
        headers: { 'Accept-Language': 'pt-BR,pt;q=0.9' }
      });
      const data = await response.json();

      if (data && data.length > 0) {
        const { lat, lon } = data[0];
        const newPos = { lat: parseFloat(lat), lng: parseFloat(lon) };
        setMapMarker(newPos);
        setFormData(prev => ({
          ...prev,
          latitude: lat,
          longitude: lon
        }));

        // Foca o mapa na nova posição se a ref estiver disponível
        if (mapRef.current) {
          mapRef.current.flyTo(newPos, 16);
        }
      } else {
        setErrors(prev => ({ ...prev, address: 'Endereço não encontrado no mapa.' }));
      }
    } catch (err) {
      console.error('Erro ao buscar endereço', err);
      setErrors(prev => ({ ...prev, address: 'Erro ao conectar-se ao serviço de mapas.' }));
    } finally {
      setIsGeocoding(false);
    }
  };

  // Handler para mudança de hierarquia
  const handleHierarchyChange = (field, memberId) => {
    setFormData(prev => ({
      ...prev,
      hierarchy: {
        ...prev.hierarchy,
        [field]: memberId,
      },
    }));
  };

  // Carrega todos os membros para a lógica de hierarquia
  const { data: allMembers } = useQuery({
    queryKey: ['members-all'],
    queryFn: async () => {
      const response = await api.get('/members', { params: { per_page: 999 } });
      return response.data.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutos de cache
  });

  // Handler para mudança de líder
  const handleLeaderChange = (leaderId) => {
    // Atualiza o líder
    const newHierarchy = { ...formData.hierarchy };

    // Se tiver um líder selecionado e temos a lista de membros
    if (leaderId && allMembers) {
      let currentMember = allMembers.find(m => m.id === parseInt(leaderId));

      // Mapeamento de roles para campos
      const hierarchyFields = {
        'discipulador_celula': 'discipulador_id',
        'pastor_rede': 'pastor_rede_id',
        'pastor_senior': 'pastor_senior_id',
        'pastor_supervisor': 'pastor_supervisor_id'
      };

      // Limpa campos da hierarquia para preencher novamente
      Object.keys(hierarchyFields).forEach(key => {
        newHierarchy[hierarchyFields[key]] = null;
      });

      // Percorre a hierarquia (ancestrais)
      // Set para evitar loops infinitos
      const visited = new Set();

      while (currentMember && currentMember.supervisor_id) {
        if (visited.has(currentMember.id)) break;
        visited.add(currentMember.id);

        const supervisor = allMembers.find(m => m.id === currentMember.supervisor_id);
        if (!supervisor) break;

        // Verifica roles do supervisor
        if (supervisor.roles && supervisor.roles.length > 0) {
          supervisor.roles.forEach(role => {
            if (hierarchyFields[role.slug]) {
              const fieldName = hierarchyFields[role.slug];
              // Se o campo ainda não foi preenchido, preenche com o ancestral mais próximo (que estamos visitando agora)
              if (!newHierarchy[fieldName]) {
                newHierarchy[fieldName] = supervisor.id;
              }
            }
          });
        }

        // Sobe para o próximo nível
        currentMember = supervisor;
      }
    }

    setFormData(prev => ({
      ...prev,
      leader_id: leaderId,
      hierarchy: newHierarchy
    }));
  };

  // Handler de submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    // Validação básica
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Nome da célula é obrigatório';
    }
    if (!formData.leader_id) {
      newErrors.leader_id = 'Líder da célula é obrigatório';
    }
    if (formData.day_of_week && (parseInt(formData.day_of_week) < 0 || parseInt(formData.day_of_week) > 6)) {
      newErrors.day_of_week = 'Dia da semana deve ser entre 0 (Domingo) e 6 (Sábado)';
    }
    if (formData.max_capacity && parseInt(formData.max_capacity) < 0) {
      newErrors.max_capacity = 'Capacidade máxima deve ser um número positivo';
    }
    if (formData.latitude && (parseFloat(formData.latitude) < -90 || parseFloat(formData.latitude) > 90)) {
      newErrors.latitude = 'Latitude deve estar entre -90 e 90';
    }
    if (formData.longitude && (parseFloat(formData.longitude) < -180 || parseFloat(formData.longitude) > 180)) {
      newErrors.longitude = 'Longitude deve estar entre -180 e 180';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Prepara dados para envio
    const submitData = {
      name: formData.name.trim(),
      description: formData.description.trim() || null,
      address: formData.address.trim() || null,
      neighborhood: formData.neighborhood.trim() || null,
      latitude: formData.latitude ? parseFloat(formData.latitude) : null,
      longitude: formData.longitude ? parseFloat(formData.longitude) : null,
      day_of_week: formData.day_of_week !== '' ? parseInt(formData.day_of_week) : null,
      start_time: formData.start_time.trim() || null,
      max_capacity: formData.max_capacity ? parseInt(formData.max_capacity) : null,
      leader_id: formData.leader_id, // Adicionado leader_id
    };

    // Adiciona is_active apenas na edição
    if (isEdit) {
      submitData.is_active = formData.is_active ? 1 : 0;
    }

    // Sempre envia o objeto hierarchy na edição para permitir limpar campos,
    // ou na criação se pelo menos um campo estiver preenchido.
    if (isEdit || Object.values(formData.hierarchy).some(id => id !== null)) {
      submitData.hierarchy = {
        discipulador_id: formData.hierarchy.discipulador_id || null,
        pastor_rede_id: formData.hierarchy.pastor_rede_id || null,
        pastor_senior_id: formData.hierarchy.pastor_senior_id || null,
        pastor_supervisor_id: formData.hierarchy.pastor_supervisor_id || null,
      };
    }

    cellMutation.mutate(submitData);
  };

  // Dias da semana
  const daysOfWeek = [
    { value: '', label: 'Selecione...' },
    { value: '0', label: 'Domingo' },
    { value: '1', label: 'Segunda-feira' },
    { value: '2', label: 'Terça-feira' },
    { value: '3', label: 'Quarta-feira' },
    { value: '4', label: 'Quinta-feira' },
    { value: '5', label: 'Sexta-feira' },
    { value: '6', label: 'Sábado' },
  ];

  if (isEdit && loadingCell) {
    return (
      <AppLayout>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '50vh' }}>
          <LoadingSpinner size="lg" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="cell-form-page" style={{ padding: '2rem', maxWidth: '800px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '2rem' }}>
          <button
            className="btn btn-text"
            onClick={() => navigate('/cells')}
            style={{ marginBottom: '1rem' }}
          >
            <span className="material-symbols-outlined">arrow_back</span>
            Voltar
          </button>
          <h1 style={{ fontSize: '1.875rem', fontWeight: 'bold' }}>
            {isEdit ? 'Editar Célula' : 'Adicionar Célula'}
          </h1>
        </div>

        {/* Erro geral */}
        {errors.general && (
          <div className="alert alert-error" style={{ marginBottom: '1.5rem' }}>
            {errors.general}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Informações Básicas */}
          <div className="card" style={{ padding: '2rem', marginBottom: '1.5rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem' }}>
              Informações Básicas
            </h2>

            {/* Nome */}
            <div className="form-group">
              <label className="form-label">
                Nome da Célula <span style={{ color: 'var(--danger)' }}>*</span>
              </label>
              <input
                type="text"
                name="name"
                className={`form-input ${errors.name ? 'error' : ''}`}
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="Ex: Célula Centro"
              />
              {errors.name && (
                <span style={{ color: 'var(--danger)', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
                  {errors.name}
                </span>
              )}
            </div>

            {/* Líder da Célula */}
            <div className="form-group" style={{ marginTop: '1rem' }}>
              <MemberSelector
                label="Líder da Célula"
                placeholder="Selecione o líder da célula..."
                value={formData.leader_id}
                onChange={(id) => handleLeaderChange(id)}
                role="lider_celula,discipulador_celula,pastor_rede,pastor_senior,pastor_supervisor"
                required
                error={errors.leader_id}
              />
            </div>

            {/* Descrição */}
            <div className="form-group" style={{ marginTop: '1rem' }}>
              <label className="form-label">Descrição</label>
              <textarea
                name="description"
                className="form-input"
                value={formData.description}
                onChange={handleChange}
                rows={4}
                placeholder="Descreva a célula, seu propósito, características..."
              />
            </div>
          </div>

          {/* Localização */}
          <div className="card" style={{ padding: '2rem', marginBottom: '1.5rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem' }}>
              Localização
            </h2>

            {/* Endereço */}
            <div className="form-group">
              <label className="form-label">Endereço</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input
                  type="text"
                  name="address"
                  className={`form-input ${errors.address ? 'error' : ''}`}
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="Ex: Rua das Flores, 123"
                  style={{ flex: 1 }}
                />
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={handleGeocode}
                  disabled={isGeocoding}
                  title="Encontrar no mapa"
                >
                  <span className="material-symbols-outlined">
                    {isGeocoding ? 'hourglass_empty' : 'search'}
                  </span>
                </button>
              </div>
              {errors.address && (
                <span style={{ color: 'var(--danger)', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
                  {errors.address}
                </span>
              )}
            </div>

            {/* Bairro */}
            <div className="form-group" style={{ marginTop: '1rem' }}>
              <label className="form-label">Bairro</label>
              <input
                type="text"
                name="neighborhood"
                className="form-input"
                value={formData.neighborhood}
                onChange={handleChange}
                placeholder="Ex: Centro"
              />
            </div>

            {/* Coordenadas */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '1rem' }}>
              <div className="form-group">
                <label className="form-label">Latitude</label>
                <input
                  type="number"
                  name="latitude"
                  className={`form-input ${errors.latitude ? 'error' : ''}`}
                  value={formData.latitude}
                  onChange={handleChange}
                  step="any"
                  placeholder="Ex: -23.5505"
                />
                {errors.latitude && (
                  <span style={{ color: 'var(--danger)', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
                    {errors.latitude}
                  </span>
                )}
                <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>
                  Opcional - Para exibir no mapa
                </p>
              </div>

              <div className="form-group">
                <label className="form-label">Longitude</label>
                <input
                  type="number"
                  name="longitude"
                  className={`form-input ${errors.longitude ? 'error' : ''}`}
                  value={formData.longitude}
                  onChange={handleChange}
                  step="any"
                  placeholder="Ex: -46.6333"
                />
                {errors.longitude && (
                  <span style={{ color: 'var(--danger)', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
                    {errors.longitude}
                  </span>
                )}
                <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>
                  Opcional - Para exibir no mapa
                </p>
              </div>
            </div>

            {/* Mapa Preview */}
            <div style={{ marginTop: '1.5rem', height: '300px', width: '100%', borderRadius: '8px', overflow: 'hidden', border: '1px solid var(--border)' }}>
              <MapContainer
                center={mapMarker ? [mapMarker.lat, mapMarker.lng] : [38.7223, -9.1393]} // Lisboa default
                zoom={mapMarker ? 16 : 11}
                style={{ height: '100%', width: '100%', zIndex: 1 }}
                ref={mapRef}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <LocationMarker position={mapMarker} setPosition={handleMapClick} />
              </MapContainer>
            </div>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginTop: '0.5rem', textAlign: 'center' }}>
              Clique no mapa para marcar a localização exata da célula.
            </p>
          </div>

          {/* Horário e Capacidade */}
          <div className="card" style={{ padding: '2rem', marginBottom: '1.5rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem' }}>
              Horário e Capacidade
            </h2>

            {/* Dia da Semana e Horário */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div className="form-group">
                <label className="form-label">Dia da Semana</label>
                <select
                  name="day_of_week"
                  className={`form-input ${errors.day_of_week ? 'error' : ''}`}
                  value={formData.day_of_week}
                  onChange={handleChange}
                >
                  {daysOfWeek.map(day => (
                    <option key={day.value} value={day.value}>{day.label}</option>
                  ))}
                </select>
                {errors.day_of_week && (
                  <span style={{ color: 'var(--danger)', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
                    {errors.day_of_week}
                  </span>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Horário de Início</label>
                <input
                  type="time"
                  name="start_time"
                  className="form-input"
                  value={formData.start_time}
                  onChange={handleChange}
                />
                <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>
                  Ex: 19:00
                </p>
              </div>
            </div>

            {/* Capacidade Máxima */}
            <div className="form-group" style={{ marginTop: '1rem' }}>
              <label className="form-label">Capacidade Máxima</label>
              <input
                type="number"
                name="max_capacity"
                className={`form-input ${errors.max_capacity ? 'error' : ''}`}
                value={formData.max_capacity}
                onChange={handleChange}
                min="0"
                placeholder="Ex: 30"
              />
              {errors.max_capacity && (
                <span style={{ color: 'var(--danger)', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
                  {errors.max_capacity}
                </span>
              )}
              <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>
                Número máximo de pessoas que a célula pode acomodar
              </p>
            </div>
          </div>

          {/* Hierarquia */}
          <div className="card" style={{ padding: '2rem', marginBottom: '1.5rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem' }}>
              Hierarquia Organizacional
            </h2>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginBottom: '1.5rem' }}>
              Defina a cadeia hierárquica de supervisão desta célula. Os membros devem estar cadastrados no sistema.
            </p>

            <div style={{ display: 'grid', gap: '1.5rem' }}>
              {/* Discipulador */}
              <MemberSelector
                value={formData.hierarchy.discipulador_id}
                onChange={(id) => handleHierarchyChange('discipulador_id', id)}
                label="Discipulador"
                placeholder="Buscar discipulador..."
                role="discipulador_celula"
              />

              {/* Pastor de Rede */}
              <MemberSelector
                value={formData.hierarchy.pastor_rede_id}
                onChange={(id) => handleHierarchyChange('pastor_rede_id', id)}
                label="Pastor de Rede"
                placeholder="Buscar pastor de rede..."
                role="pastor_rede"
              />

              {/* Pastor Senior */}
              <MemberSelector
                value={formData.hierarchy.pastor_senior_id}
                onChange={(id) => handleHierarchyChange('pastor_senior_id', id)}
                label="Pastor Senior"
                placeholder="Buscar pastor senior..."
                role="pastor_senior"
              />

              {/* Pastor Supervisor */}
              <MemberSelector
                value={formData.hierarchy.pastor_supervisor_id}
                onChange={(id) => handleHierarchyChange('pastor_supervisor_id', id)}
                label="Pastor Supervisor"
                placeholder="Buscar pastor supervisor..."
                role="pastor_supervisor"
              />
            </div>

            {errors.hierarchy && (
              <div style={{
                marginTop: '1rem',
                padding: '0.75rem',
                backgroundColor: 'var(--danger-light)',
                borderRadius: '8px',
                fontSize: '0.875rem',
                color: 'var(--danger)',
                border: '1px solid var(--danger)'
              }}>
                {errors.hierarchy}
              </div>
            )}

            <div style={{
              marginTop: '1rem',
              padding: '1rem',
              backgroundColor: 'var(--info-light)',
              borderRadius: '8px',
              fontSize: '0.875rem',
              color: 'var(--info-dark)'
            }}>
              <strong>Nota:</strong> A hierarquia será criada automaticamente ao salvar a célula.
              Certifique-se de que a célula possui um líder definido antes de salvar.
            </div>
          </div>

          {/* Status (apenas na edição) */}
          {isEdit && (
            <div className="card" style={{ padding: '2rem', marginBottom: '1.5rem' }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem' }}>
                Status
              </h2>

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleChange}
                  />
                  <span>Célula ativa</span>
                </label>
                <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>
                  Células inativas não aparecerão em algumas listagens e relatórios
                </p>
              </div>
            </div>
          )}

          {/* Botões de Ação */}
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate('/cells')}
              disabled={cellMutation.isPending}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={cellMutation.isPending}
            >
              {cellMutation.isPending ? (
                <>
                  <LoadingSpinner size="sm" />
                  Salvando...
                </>
              ) : (
                <>
                  <span className="material-symbols-outlined">save</span>
                  {isEdit ? 'Salvar Alterações' : 'Criar Célula'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </AppLayout>
  );
}

export default CellFormPage;
